import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { organizerService as sharedOrganizerService } from "../../../shared/api/organizerService";
import { PermissionName } from "../../../shared/types/permission";
import { organizerService } from "../shared/api/organizerService";
import { OrganizerIndexResource } from "../shared/types";
import { OrganizerCreateDrawer, OrganizerUpdateDrawer } from "./OrganizerDrawer";
import { OrganizerListContextMenu } from "./OrganizerListContextMenu";

export const OrganizersList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		organizerService,
		{
			entityName: t("Organizer"),
			messages: {
				create: t("Organizer was successfully created."),
				update: t("Organizer was successfully updated."),
				delete: t("Organizer was successfully deleted."),
			},
		},
		[],
		[sharedOrganizerService.quickFilterKey],
	);

	const dataListColumns: IDrawerDataListColumn<OrganizerIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Type"),
				valueKey: "type",
				type: "value",
				sortable: true,
				render: (organizer) => t(organizer.type),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<OrganizerListContextMenu
						organizerId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);
	const canCreate = useHasPermission<PermissionName>(["organizers.create"]);
	const canUpdate = useHasPermission<PermissionName>(["organizers.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{
				search: "",
				searchFields: [{ name: "name", label: t("Name") }],
			}}
			translations={{
				title: t("Organizers"),
				empty: t("No organizers available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<OrganizerCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<OrganizerUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			initialSorting={{ column: "name", direction: "asc" }}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(organizerService.listKey, organizerService.index)}
		/>
	);
};
